import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, FunnelSimple, Package, ArrowClockwise, Fire } from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LoadCard } from "@/components/LoadCard";
import { RequestLoadDialog } from "@/components/RequestLoadDialog";
import { PostLoadDialog } from "@/components/PostLoadDialog";
import { LeadDialog } from "@/components/LeadDialog";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { getLoads, EQUIPMENT } from "@/lib/api";

const SORTS = [
  { v: "newest", l: "Newest" },
  { v: "highest_rate", l: "Highest Rate" },
  { v: "rate_per_mile", l: "$/Mile" },
  { v: "shortest_miles", l: "Shortest Miles" },
];

const QUICK = [
  { value: "All", label: "All Loads" },
  { value: "Hotshot", label: "Hot Shot", hot: true },
  { value: "Expedited", label: "Expedited" },
  { value: "Reefer", label: "Reefer" },
  { value: "Flatbed", label: "Flatbed" },
  { value: "Dry Van", label: "Dry Van" },
];

export default function LoadBoard() {
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reqLoad, setReqLoad] = useState(null);
  const [postOpen, setPostOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [filters, setFilters] = useState({ origin_city: "", dest_city: "", equipment: "All", min_rate: "", min_rate_per_mile: "", sort: "newest" });

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const fetchLoads = useCallback(async () => {
    setLoading(true);
    const params = { sort: filters.sort, status: "open" };
    if (filters.origin_city) params.origin_city = filters.origin_city;
    if (filters.dest_city) params.dest_city = filters.dest_city;
    if (filters.equipment && filters.equipment !== "All") params.equipment = filters.equipment;
    if (filters.min_rate) params.min_rate = parseFloat(filters.min_rate);
    if (filters.min_rate_per_mile) params.min_rate_per_mile = parseFloat(filters.min_rate_per_mile);
    try {
      setLoads(await getLoads(params));
    } catch {
      setLoads([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(fetchLoads, 250);
    return () => clearTimeout(t);
  }, [fetchLoads]);

  const reset = () => setFilters({ origin_city: "", dest_city: "", equipment: "All", min_rate: "", min_rate_per_mile: "", sort: "newest" });

  return (
    <div className="min-h-screen bg-[#0B1220] text-white">
      <Navbar onQuote={() => setQuoteOpen(true)} />

      <div className="pt-28 pb-6 border-b border-white/10 bg-[#111A2E]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="text-[#2563EB] uppercase text-xs tracking-[0.2em] mb-2">Live marketplace</div>
            <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Load Board</h1>
          </div>
          <button onClick={() => setPostOpen(true)} data-testid="board-post-load-btn" className="px-6 py-3 rounded-full bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] transition-colors flex items-center gap-2 self-start">
            <Package size={20} /> Post a Load
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8">
        {/* FILTERS */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="bg-[#111A2E] border border-white/10 rounded-2xl p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold flex items-center gap-2"><FunnelSimple size={18} className="text-[#2563EB]" /> Filters</h3>
              <button onClick={reset} data-testid="reset-filters-btn" className="text-xs text-[#94A3B8] hover:text-[#2563EB] flex items-center gap-1"><ArrowClockwise size={13} /> Reset</button>
            </div>
            <FField label="Origin">
              <div className="relative">
                <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748b]" />
                <Input data-testid="filter-origin" value={filters.origin_city} onChange={(e) => set("origin_city", e.target.value)} placeholder="City" className="bg-[#0B1220] border-white/10 pl-9" />
              </div>
            </FField>
            <FField label="Destination">
              <div className="relative">
                <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748b]" />
                <Input data-testid="filter-dest" value={filters.dest_city} onChange={(e) => set("dest_city", e.target.value)} placeholder="City" className="bg-[#0B1220] border-white/10 pl-9" />
              </div>
            </FField>
            <FField label="Equipment">
              <Select value={filters.equipment} onValueChange={(v) => set("equipment", v)}>
                <SelectTrigger data-testid="filter-equipment" className="bg-[#0B1220] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111A2E] border-white/10 text-white">
                  <SelectItem value="All">All Equipment</SelectItem>
                  {EQUIPMENT.map((eq) => <SelectItem key={eq} value={eq}>{eq}</SelectItem>)}
                </SelectContent>
              </Select>
            </FField>
            <div className="grid grid-cols-2 gap-3">
              <FField label="Min Rate"><Input type="number" data-testid="filter-min-rate" value={filters.min_rate} onChange={(e) => set("min_rate", e.target.value)} placeholder="$" className="bg-[#0B1220] border-white/10" /></FField>
              <FField label="Min $/mi"><Input type="number" data-testid="filter-min-rpm" value={filters.min_rate_per_mile} onChange={(e) => set("min_rate_per_mile", e.target.value)} placeholder="$" className="bg-[#0B1220] border-white/10" /></FField>
            </div>
            <FField label="Sort By">
              <Select value={filters.sort} onValueChange={(v) => set("sort", v)}>
                <SelectTrigger data-testid="filter-sort" className="bg-[#0B1220] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111A2E] border-white/10 text-white">
                  {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </FField>
          </div>
        </aside>

        {/* RESULTS */}
        <main>
          <div className="flex flex-wrap gap-2 mb-5" data-testid="quick-filter-chips">
            {QUICK.map((q) => {
              const active = filters.equipment === q.value;
              const base = "px-4 py-2 rounded-full text-sm font-medium border transition-colors flex items-center gap-1.5";
              const cls = active
                ? "bg-[#2563EB] text-white border-[#2563EB]"
                : q.hot
                ? "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/40 hover:bg-[#F59E0B]/20"
                : "bg-[#111A2E] text-[#94A3B8] border-white/10 hover:border-[#2563EB]/50 hover:text-white";
              return (
                <button key={q.value} data-testid={`chip-${q.value.toLowerCase().replace(/\s/g, "-")}`} onClick={() => set("equipment", q.value)} className={`${base} ${cls}`}>
                  {q.hot && <Fire size={15} weight="fill" />} {q.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between mb-5">
            <div className="text-sm text-[#94A3B8]" data-testid="result-count">
              {loading ? "Searching…" : <><span className="text-[#2563EB] font-semibold text-base">{loads.length}</span> loads available</>}
            </div>
          </div>

          {loading ? (
            <div className="space-y-4">{[...Array(4)].map((_, i) => <div key={i} className="h-40 rounded-xl bg-[#111A2E] border border-white/10 animate-pulse" />)}</div>
          ) : loads.length === 0 ? (
            <div className="text-center py-24 bg-[#111A2E] border border-white/10 rounded-2xl" data-testid="empty-state">
              <Package size={48} className="text-[#64748b] mx-auto mb-4" />
              <h3 className="font-display text-xl font-bold mb-2">No loads match — widen your filters</h3>
              <p className="text-[#94A3B8] mb-6">Try clearing a filter or post a load of your own.</p>
              <button onClick={() => setPostOpen(true)} className="px-6 py-3 rounded-full bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] transition-colors">Post a Load</button>
            </div>
          ) : (
            <div className="space-y-4">
              {loads.map((l, i) => <LoadCard key={l.id} load={l} index={i} onRequest={setReqLoad} />)}
            </div>
          )}
        </main>
      </div>

      <Footer />
      <RequestLoadDialog open={!!reqLoad} onOpenChange={(v) => !v && setReqLoad(null)} load={reqLoad} />
      <PostLoadDialog open={postOpen} onOpenChange={setPostOpen} onPosted={fetchLoads} />
      <LeadDialog open={quoteOpen} onOpenChange={setQuoteOpen} type="quote" />
    </div>
  );
}

const FField = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs text-[#94A3B8]">{label}</label>
    {children}
  </div>
);
